import { BADGE_VARIANTS } from 'ui/badge';
import { BADGE_VARIANT_TYPES } from 'ui/badge/types';

import { Durations, GoalStatus, InternalTemplates } from './types';

export const ACTIONS = 'Actions';
export const PREVIEW_USERS_USER = 'user';
export const PREVIEW_USERS_USERS = 'users';
export const PREVIEW_USERS_NO_USERS = 'No users';
export const PREVIEW_USERS_MET_CRITERIA = 'recently met this criteria. We require at least 1k.';
export const PREVIEW_USERS_ONLY = 'Only';
export const PREVIEW_USERS_APPROX = 'Approx';
export const PREVIEW_USERS_ERROR_LEAD = `Hmm... we're having some trouble.`;
export const PREVIEW_USERS_ERROR_BODY = `We couldn't load your estimated user count. Please try again.`;
export const PREVIEW_USERS_DISABLED_HINT = 'Enter goal criteria to preview user count.';
export const DESCRIPTION_CAPTION =
  'Project users will be scored in their profile with this field name.';
export const NEW_GOAL = 'New goal';
export const NEW_PREDICTIVE_GOAL = 'New Predictive Goal';
export const UPDATE_GOAL = 'Update goal';
export const CREATE_GOAL = 'Create goal';
export const CANCEL_GOAL_UPDATE = 'Cancel';
export const GOAL_NAME = 'Goal name';
export const GOAL_FIELD_NAME = 'User profile field name';
export const PREDICTIVE_GOALS = 'Predictive Goals';
export const DELETE_PREDICTIVE_GOALS_TOAST_MESSAGE =
  'Deleting predictive goal. It can take up to 24 hours to remove the user field from each of your users';
export const PROVIDE_GOAL_NAME = 'Please enter a predictive goal name';
export const PROVIDE_USER_FIELD = 'Please enter a user field';
export const PROVIDE_GOAL_DEFINITION = "Please construct a query for the goal you'd like to target";
export const PROVIDE_VALID_GOAL_NAME =
  'We’re having trouble creating your goal. Please remove spaces from Predictive goal name and try again.';
export const PROVIDE_VALID_CONTACT_PROPERTY_NAME =
  'We’re having trouble creating your goal. Please remove spaces from the user profile field name and try again.';
export const UNSAVED_CHANGES_PROMPT = 'Leave page? Changes you made may not be saved.';
export const MALFORMED_PROPERTY_QUERY =
  'Malformed user field. Please check your user fields in the query.';
export const MALFORMED_EVENT_QUERY = 'Malformed event. Please check your events in the query.';
export const MALFORMED_OCCURRENCES_CONDITION =
  'Occurrence condition is missing a comparator or value. Please check your events in the query.';
export const MALFORMED_PURCHASE_CONDITION =
  'Purchase condition is missing a comparator or value. Please check your events in the query.';
export const constructNumericalValueForPropertyValidation = (propertyName: string) =>
  `Must enter a numerical value for ${propertyName}`;
export const NO_DESCRIPTION = 'No description';
export const PREDICTIVE_GOALS_KEY = 'predictiveAudiencesSegmentation';
export const PREDICTIVE_GOALS_PAGE_INFO_TEXT =
  'Predictive Goals can help you understand whether your users are likely to convert on your unique business goals. Use these insights to customize your messages and drive conversions.';
export const PREDICTIVE_GOALS_PAGE_INFO_HEADER_TEXT =
  'Predictions that inform your messaging strategy';
export const PREDICTIVE_GOALS_TEMPLATES_HEADER = 'Start from a common business goal';
export const PREDICTIVE_GOALS_LIKELY_TO_ENGAGE = 'Likely to engage';
export const PREDICTIVE_GOALS_LIKELY_TO_CHURN = 'Likely to churn';
export const PREDICTIVE_GOALS_LIKELY_TO_PURCHASE = 'Likely to purchase';
export const PREDICTIVE_GOALS_LIKELY_TO_ENGAGE_DETAILS =
  'Predict which users are most likely click and engage with your email';
export const PREDICTIVE_GOALS_LIKELY_TO_CHURN_DETAILS =
  'Predict which users are most likely unsubscribe or cancel membership';
export const PREDICTIVE_GOALS_LIKELY_TO_PURCHASE_DETAILS =
  'Predict which users are most likely make a purchase';
export const PREDICTIVE_STRENGTH = 'Predictive strength';
export const MAX_GOAL_LIMIT = 6;
export const MAX_GOAL_TOOLTIP_CONTENT =
  'Each project can have up to six predictive goals. To create a new goal, first delete an existing one.';
export const MAX_GOAL_BANNER =
  "You've reached your maximum amount of goals for this project. To create a new goal, first delete an existing one.";
export const MAX_GOALS_ON_CREATE =
  'Each project can have up to six predictive goals. To create a new goal, first delete an existing one.';
export const constructGoalLimitResponse = (limit: number = MAX_GOAL_LIMIT) =>
  `Goal limit of ${limit} reached`;
export const constructGoalUniqueNameAndPropertyResponseErrorMsg =
  'Goal name and user profile field name must be unique.';
export const GOAL_CREATION_LIMIT_ERROR =
  'Each project can have up to six predictive goals. To create a new goal, first delete an existing one.';
export const GOAL_DELETE_ERROR =
  "We're having trouble deleting your goal. Please try again or contact support.";
export const GOAL_CREATE_ERROR =
  "We're having trouble creating your goal. Please try again or contact support.";
export const GENERAL_ERROR = 'Oops… it looks like there was an issue';
export const GOAL_UPDATE_RETRAIN_CONFIRMATION =
  'Re-training your predictive goal. It can take up to 24 hours to update based on your new goal criteria.';
export const GOAL_UPDATE_CONFIRMATION =
  "You've successfully updated the goal description and name.";
export const INDEX_PAGE_TITLE = 'Start a new predictive goal';
export const INDEX_PAGE_CREATE_TEXT = 'Already have a goal in mind?';
export const INDEX_PAGE_CREATE_BUTTON_TEXT = 'Start from scratch';
export const INITIAL_PAGE_DISABLED_TEMPLATE_TEXT =
  'Your project does not currently track the event needed for this template.';
export const EXIST_GOALS_DISABLED_TEMPLATE_TEXT =
  'Your project does not currently track the event needed for this template.';
export const RBAC_USER_LACKS_MANAGE_PERMISSION =
  "You don't have user permission for this action. Please check with your Admin.";
export const PROJECT_VOCABULARY_NOT_READY_HEADER = 'Prepping your project for Predictive Goals';
export const PROJECT_VOCABULARY_NOT_READY_BANNER_BOLD_TEXT =
  'Prepping your project for Predictive Goals.';
export const GOAL_NEEDS_REVIEW_DEFAULT =
  "We're unable to create a predictive goal. Please change your goal criteria and/or check if the associated events/user fields have sufficient data.";
export const GOAL_CRITERIA = 'Goal criteria';
export const GOAL_DETAILS = 'Goal details';
export const EDIT_GOAL = 'Edit goal';
export const DELETE_GOAL = 'Delete goal';

export const LAST_REFRESH_TOOLTIP = 'We regularly refresh predictions to include the latest data.';
export const PREDICTIVE_STRENGTH_TOOLTIP =
  'Strength is measured as strong, satisfactory, or weak to evaluate prediction effectiveness.';
export const SYSTEM_ERROR_BANNER_TITLE = 'Experiencing system errors';
export const SYSTEM_ERROR_BANNER_BODY =
  'We’re unable to create your predictive goal. Please reach out to Iterable Support for assistance. Thank you for your patience!';
export const EDITING_GOAL_BANNER_MESSAGE =
  'Updating your goal criteria will require that we re-create your predictive goal and calculate new conversion probability scores for your users.';
export const XAI_HEADER_TILE_TEXT = 'Understand your prediction';
export const XAI_FEATURES_HEADER_TEXT = `Events and user fields most likely to...`;
export const XAI_LIKELY_EMPTY_STATE_TEXT = 'No events or user fields';
export const XAI_TOP_FEATURES_EMPTY_STATE_TEXT_CONTENT =
  'Explainable AI data unavailable until next refresh.';
export const XAI_LEFT_NAV_SLIDEOVER_BUTTON_TEXT_CONTENT = 'Explore';
export const XAI_LIKELY_TO_INCREASE_TOOLTIP_TEXT = `More of these events and user fields may increase the likelihood of your predictive goal outcome.`;
export const XAI_LIKELY_TO_DECREASE_TOOLTIP_TEXT = `More of these events and user fields may decrease the likelihood of your predictive goal outcome.`;
export const XAI_LIKELY_TILE_LABEL_TEXT = ' goal outcome';
export const XAI_DONUT_CHART_CARD_HEADER_TEXT = 'Events & user fields';
export const XAI_DONUT_CHART_HEADER_TEXT =
  'Breakdown of the events and user fields shaping your prediction';
export const XAI_MOST_MEANINGFUL = 'Most Meaningful';
export const XAI_CONTRIBUTING_EVENTS_AND_USER_FIELDS = 'contributing events and user fields.';
export const XAI_TOOLTIP =
  'The events and user fields assessed and determined to have the most contribution in your conversion prediction.';
export const SEE_LESS_EXPAND_BUTTON_TEXT = 'See less';
export const SEE_MORE_EXPAND_BUTTON_TEXT = 'See more';
export const RANGE_SLIDER_CONTACT_SELECTION = 'User Selection';
export const MOST_LIKELY_TO_CONVERT = 'Most likely to convert';
export const LEAST_LIKELY_TO_CONVERT = 'Least likely to convert';
export const BOTTOM_10_PERCENT = 'Bottom 10%';
export const TOP_10_PERCENT = 'Top 10%';
export const CUSTOM_RANGE = 'Custom Range';
export const PREDICTION = 'Prediction';
export const REFRESH_CAPTION = 'Your prediction will be updated when the refresh completes.';
export const USER_FIELD = 'User field';
export const BUILDING_PRD_GOAL = 'Building your predictive goal';
export const IN_PROGRESS_STATUS =
  'It may take up to 24 hours to create this predictive goal and calculate a conversion probability for each of your users. We’ll send you a notification in Iterable when it’s finished.';
export const WHEN_READY = 'When the predictive goal is ready...';
export const CREATE_SEGMENT = 'Create segments using the new';
export const VIEW_THE = 'View the';
export const SCORE_ON_USERS = 'score on user profile pages.';
export const VIEW_GOAL_DETAILS =
  'View the goal details page to see the predictive scores and distribution.';
/**
@description Input placeholders
*/
export const PLACEHOLDER_SELECT_AN_EVENT = 'Select an event...';
export const PLACEHOLDER_SELECT_A_USER_FIELD = 'Select a user field';
export const PLACEHOLDER_SELECT_A_VALUE = 'Select a value...';
export const SELECT_A_USER_FIELD = 'Select a user field';
export const SEARCH_USER_FIELDS = 'Search user fields';
export const EVENT_OR_PROPERTY_LEADING_TEXT = {
  event: 'Perform event',
  property: 'Have user field',
};
export const EVENT_OR_PROPERTY_TRAILING_TEXT = {
  event: 'Perform event',
  property: 'Have user field',
};
export const HAVE_USER_FIELD = 'Have user field';
export const USER_PROFILE_FIELD = 'User profile field';
export const USERS_SELECTED = 'users selected';
export const DIST_SELECTION_MORE_LIKELY_TO_CONVERT = 'x more likely to convert than avg.';
export const APPROX = 'Approx';
export const CONVERSIONS_PREDICTED = 'conversions predicted';
export const VIEW_SELECTION_IN_SEGMENTATION = 'View selection in segmentation';
export const CREATE_MODAL_CONFIRMATION_HEADER = 'Confirm before continuing';
export const CREATE_MODAL_CONFIRMATION_BODY_TEXT =
  'It may take up to 24 hours to create this predictive goal and score each of your users with a conversion probability.';
export const CREATE_MODAL_CONFIRMATION_BODY_FOLLOWING_TEXT =
  'Before you proceed, make sure that the goal is correctly configured.';
export const CREATE_MODAL_CONFIRMATION_CANCEL_BTN_TEXT = 'Cancel';
export const CREATE_MODAL_CONFIRMATION_CONFIRM_BTN_TEXT = 'Confirm and create';

export const GOAL_STATUS_TEXT: Record<GoalStatus, string> = {
  [GoalStatus.NEEDS_REVIEW]: 'Needs Review',
  [GoalStatus.SYSTEM_ERROR]: 'System Error',
  [GoalStatus.READY]: 'Ready',
  [GoalStatus.PREPARING]: 'Preparing',
  [GoalStatus.REFRESHING]: 'Refreshing',
  [GoalStatus.DELETING]: 'Deleting',
};
export const GOAL_STATUS_BADGE_CONFIG: Record<
  GoalStatus,
  { variant: BADGE_VARIANT_TYPES; text: string }
> = {
  [GoalStatus.NEEDS_REVIEW]: {
    variant: BADGE_VARIANTS.DRAFT,
    text: GOAL_STATUS_TEXT[GoalStatus.NEEDS_REVIEW],
  },
  [GoalStatus.SYSTEM_ERROR]: {
    variant: BADGE_VARIANTS.DRAFT,
    text: GOAL_STATUS_TEXT[GoalStatus.SYSTEM_ERROR],
  },
  [GoalStatus.READY]: {
    variant: BADGE_VARIANTS.READY,
    text: GOAL_STATUS_TEXT[GoalStatus.READY],
  },
  [GoalStatus.PREPARING]: {
    variant: BADGE_VARIANTS.RUNNING,
    text: GOAL_STATUS_TEXT[GoalStatus.PREPARING],
  },
  [GoalStatus.REFRESHING]: {
    variant: BADGE_VARIANTS.RUNNING,
    text: GOAL_STATUS_TEXT[GoalStatus.REFRESHING],
  },
  [GoalStatus.DELETING]: {
    variant: BADGE_VARIANTS.ABORTED,
    text: GOAL_STATUS_TEXT[GoalStatus.DELETING],
  },
};

/** Maps time units to days for easier comparison */
export const DURATION_UNIT_DAY_CONVERSIONS: Record<Durations, number> = {
  day: 1,
  week: 7,
  month: 30,
};

export const DEFAULT_DURATION_SERIALIZED = '1_month';

export const TEST_IDS = {
  CreateGoalBtn: 'create-pg-goal-btn',
  CreateGoalNameInput: 'create-goal-name-input',
  CreateGoalContactPropertyInput: 'create-goal-contact-property-input',
  ConfirmGoalCreationModal: 'confirm-goal-creation-modal',
  EditGoalBtn: 'edit-pg-goal-btn',
  MutateGoalErrorContent: 'mutate-goal-error-content',
  ExpandTextButton: 'expand-text-button',
  DataUnavailable: 'data-unavailable',
  DeletingPage: 'deleting-page',
  DisplayTopEventsOrProperties: 'display-top-events-or-properties',
  DonutChartContainer: 'donut-chart-container',
  DonutChart: 'donut-chart',
  DeletePropertyButton: 'delete-property-selection',
  DeleteEventButton: 'delete-event-selection',
  GeneralError: 'general-error',
  GoalCriteriaContainer: 'GoalCriteriaContainer',
  GoalLimitPage: 'goal-limit-page',
  GraphMostLikelyRadioSelection: 'graph-most-likely',
  GraphLeastLikelyRadioSelection: 'graph-least-likely',
  GraphCustomRadioSelection: 'graph-custom',
  GraphMinRangeInput: 'graph-min-range-input',
  GraphMaxRangeInput: 'graph-max-range-input',
  LastRefreshCell: 'last-refresh-cell',
  LikelyToChurnIllustration: 'likely-to-churn',
  LikelyToPurchaseIllustration: 'likely-to-purchase',
  LikelyToSubscribeIllustration: 'likely-to-subscribe',
  PredictiveGoalsStrengthCell: 'predictive-strength-cell-item',
  PreparingPage: 'preparing-page',
  PreviousStrengthText: 'previous-strength-text',
  PreviewUsers: 'preview-users-count',
  ReadyPage: 'details-page',
  RefreshingStateCaption: 'refreshing-state-caption',
  RefreshingPage: 'preparing-page',
  SpinningLogo: 'ai-spinning-logo',
  StatusPageGoalCriteriaHeader: 'status-page-goal-criteria-header',
  StrengthMeterSatisfactory: 'strength-meter-satisfactory',
  StrengthMeterStrong: 'strength-meter-strong',
  StrengthMeterWeak: 'strength-meter-weak',
  TemplateDetails: 'template-details',
  TemplateTitle: 'template-title',
  Tooltip: 'tooltip-content',
  XAIContributingFeatures: 'xai-contributing-features',
  XAILikelyFeatureList: 'xai-likely-feature-list',
  XAILikelyEmptyStateText: 'xai-likely-empty-state-text',
  XAILikelyTile: 'xai-likely-tile',
  XAILeftNavCard: 'xai-left-nav-card',
  XAILeftNavViewBtn: 'xai-left-nav-view-btn',
  XAISlideOver: 'xai-slideover',
  XAISlideOverCloseBtn: 'xai-close-slideover-button',
  XAISlideOverContent: 'xai-slideover-content',
  XAISlideOvertHeader: 'xai-slideover-header',
  XAITilePredictiveStrength: 'xai-tile-predictive-strength',
  XAITileFeatureCount: 'xai-tile-feature-count',
  XAITooltipWrapper: 'xai-tooltip-wrapper',
  XAITopThreeFeatures: 'xai-top-features',
};

export const PG_MODALS = {
  UnsavedChanges: 'UnsavedChanges',
};

/** Only display Predictive Strength for these status in the Goals Table */
export const PREDICTIVE_STRENGTH_CELL_VISIBLE_STATUSES = [
  GoalStatus.READY,
  GoalStatus.NEEDS_REVIEW,
];

export const DIST_GRAPH_RADIO_OPTIONS = {
  MOST_LIKELY: 'most-likely',
  LEAST_LIKELY: 'least-likely',
  CUSTOM: 'custom',
};

export const PRESET_LEAST_LIKELY_RANGE = {
  MIN: 0,
  MAX: 0.1,
};

export const PRESET_MOST_LIKELY_RANGE = {
  MIN: 0.9,
  MAX: 1,
};

export const ITBLDS_PG_PREFIX = 'itblDS.predictiveGoals.';
export const PRG_PROBABILITY_SCORE_TYPE = 'probability';

export const EventDeltaName = {
  UNSUBSCRIBE: 'unsubscribe',
  PUSHSEND: 'push_send',
  PUSHOPEN: 'push_open',
  SMSRECEIVED: 'smsreceived',
  SMSSEND: 'smssend',
};

export const EXCLUDED_SYSTEM_EVENTS_DELTANAMES = [
  EventDeltaName.PUSHOPEN,
  EventDeltaName.PUSHSEND,
  EventDeltaName.SMSRECEIVED,
  EventDeltaName.SMSSEND,
];

export const A_SECOND_AGO = 'a second ago';
export const A_FEW_SECONDS_AGO = 'a few seconds ago';
export const A_MINUTE_AGO = 'a minute ago';
export const AN_HOUR_AGO = 'an hour ago';
export const A_DAY_AGO = 'a day ago';
export const A_MONTH_AGO = 'a month ago';
export const A_YEAR_AGO = 'a year ago';
export const MORE_THAN_A_YEAR_AGO = 'more than a year ago';

export const CHURN_TEMPLATE_NAME = 'Likely to churn';
export const CHURN_TEMPLATE_CONTACT_PROPERTY = 'Likely_to_churn';
export const ENGAGE_TEMPLATE_NAME = 'Likely to engage';
export const ENGAGE_TEMPLATE_CONTACT_PROPERTY = 'Likely_to_engage';
export const PURCHASE_TEMPLATE_NAME = 'Likely to purchase';
export const PURCHASE_TEMPLATE_CONTACT_PROPERTY = 'Likely_to_purchase';

export const TEMPLATE_EVENT_POPOVER_HIGH_USAGE = {
  action: "Click to edit, if you'd like to try a different event or user field.",
  result: "We've selected a relevant system event for you.",
};
export const TEMPLATE_EVENT_POPOVER_LOW_USAGE = {
  action: 'Once usage increases, you can come back to create this goal.',
  result:
    'We selected a relevant event, but it will need more users before you can create this goal.',
};
export const TEMPLATE_EVENT_USAGE_THRESHOLD = 1000;

export const INTERNAL_TEMPLATES_CREATE_GOAL_NAME_MAP = {
  [InternalTemplates.CHURN]: CHURN_TEMPLATE_NAME,
  [InternalTemplates.ENGAGE]: ENGAGE_TEMPLATE_NAME,
  [InternalTemplates.PURCHASE]: PURCHASE_TEMPLATE_NAME,
};

export const INTERNAL_TEMPLATES_CREATE_GOAL_CONTACT_PROPERTY_MAP = {
  [InternalTemplates.CHURN]: CHURN_TEMPLATE_CONTACT_PROPERTY,
  [InternalTemplates.ENGAGE]: ENGAGE_TEMPLATE_CONTACT_PROPERTY,
  [InternalTemplates.PURCHASE]: PURCHASE_TEMPLATE_CONTACT_PROPERTY,
};
